import api, { getImageUrl } from "..";
import { convertItemsToTiles, convertItemsToTilesMovies } from "../formatters/ItemFormatter";
import { createEffect, createSignal } from "solid-js";
import axios from "axios";
import { Buffer } from "buffer";
import { getProxyHeaders } from "../proxyHeaders";

import {
  playListPassword,
  playListUrl,
  playListUsername,
  setGlobalStream,
  setGlobalStreamType,
} from "../../../state";

export function minutesToHMM(minutes) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return hours + "h " + (remainingMinutes < 10 ? "0" : "") + remainingMinutes + "min";
}

function formatDate(dateString) {
  const parts = dateString.split("-");
  return parts[1] + "/" + parts[2] + "/" + parts[0];
}

function justYear(dateString) {
  const parts = dateString.split("-");
  return parts[0];
}

// FIXED getRecommendations function - eliminates "Data is not available" error
export function getRecommendations({ type, category_id, id }) {
  const MAX_RETRIES = 3;
  const TIMEOUT_MS = 8000; // 8 seconds per attempt
  const RETRY_DELAY = 1000; // 1 second between retries

  let typeConst = "";
  if (type === "series") {
    typeConst = "series";
  } else {
    typeConst = "vod_streams";
  }

  // Return a promise with proper error handling and retry logic
  return new Promise(async (resolve, reject) => {
    let lastError = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {

        const result = await fetchRecommendationsData(typeConst, category_id, TIMEOUT_MS);

        if (result && Array.isArray(result) && result.length > 0) {
          const convertedData = convertItemsToTilesMovies(result);

          if (convertedData && convertedData.length > 0) {
            // Safely shuffle and slice the data
            const shuffledData = [...convertedData].sort(() => 0.5 - Math.random());
            const selectedData = shuffledData.slice(0, 8);

            resolve(selectedData);
            return;
          }
        }

        throw new Error(`Empty or invalid data received on attempt ${attempt}`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Recommendation attempt ${attempt} failed:`, error.message);

        if (attempt < MAX_RETRIES) {
          // Wait before retry with exponential backoff
          const delay = RETRY_DELAY * attempt;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All attempts failed - resolve with empty array instead of rejecting
    console.warn(`⚠️ All ${MAX_RETRIES} attempts failed, returning empty recommendations`);
    console.warn("Last error was:", lastError?.message);

    // CRITICAL: Always resolve, never reject to prevent "Data is not available" error
    resolve([]);
  });
}

// Helper function to fetch recommendations data with timeout
async function fetchRecommendationsData(typeConst, category_id, timeoutMs) {
  return new Promise(async (resolve, reject) => {
    // Set up timeout
    const timeoutId = setTimeout(() => {
      reject(new Error(`Request timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    try {
      const vpnStatus = localStorage.getItem("vpn");
      const proxyUrl = localStorage.getItem("proxy_url");

      let apiUrl = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_${typeConst}&category_id=${category_id}`;

      // If VPN is on and proxy_url exists, buffer the API URL and use proxy
      if (vpnStatus === "on" && proxyUrl) {
        const bufferedUrl = Buffer.from(apiUrl, "utf-8").toString("base64");
        apiUrl = `${proxyUrl}${bufferedUrl}`;
      }

      const response = await axios.get(apiUrl, {
        timeout: timeoutMs - 1000, // Leave 1 second margin for cleanup
        validateStatus: status => status === 200,
        headers: vpnStatus === "on" && proxyUrl ? getProxyHeaders() : undefined,
      });

      clearTimeout(timeoutId);

      if (response.data && Array.isArray(response.data)) {
        resolve(response.data);
      } else {
        throw new Error(`Invalid response format: ${typeof response.data}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        reject(new Error("Network timeout - server took too long to respond"));
      } else if (error.response) {
        reject(new Error(`HTTP ${error.response.status}: ${error.response.statusText}`));
      } else {
        reject(new Error(`Network error: ${error.message}`));
      }
    }
  });
}

// FIXED getSeriesSeasons function - same pattern applied
export function getSeriesSeasons({ type, category_id, id }) {
  const MAX_RETRIES = 3;
  const TIMEOUT_MS = 8000;
  const RETRY_DELAY = 1000;

  let typeConst = type === "series" ? "series" : "vod_streams";

  return new Promise(async (resolve, reject) => {
    let lastError = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await fetchSeriesSeasonsData(typeConst, id, TIMEOUT_MS);

        if (result !== null && result !== undefined) {
          resolve(result);
          return;
        }

        throw new Error(`No series data received on attempt ${attempt}`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Series seasons attempt ${attempt} failed:`, error.message);

        if (attempt < MAX_RETRIES) {
          const delay = RETRY_DELAY * attempt;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All attempts failed - resolve with empty array instead of rejecting
    console.warn(`⚠️ All ${MAX_RETRIES} attempts failed for series seasons, returning empty array`);
    console.warn("Last error was:", lastError?.message);

    // CRITICAL: Always resolve, never reject
    resolve([]);
  });
}

// Helper function to fetch series seasons data
async function fetchSeriesSeasonsData(typeConst, id, timeoutMs) {
  return new Promise(async (resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`Request timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    try {
      const vpnStatus = localStorage.getItem("vpn");
      const proxyUrl = localStorage.getItem("proxy_url");

      let apiUrl = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_${typeConst}_info&${typeConst}_id=${id}`;

      if (vpnStatus === "on" && proxyUrl) {
        const bufferedUrl = Buffer.from(apiUrl, "utf-8").toString("base64");
        apiUrl = `${proxyUrl}${bufferedUrl}`;
      }

      const response = await axios.get(apiUrl, {
        timeout: timeoutMs - 1000,
        validateStatus: status => status === 200,
        headers: vpnStatus === "on" && proxyUrl ? getProxyHeaders() : undefined,
      });

      clearTimeout(timeoutId);

      if (response.data && typeof response.data.episodes === "object") {
        const cover = response.data.info.cover;
        const seasonsFromEpisodes = Object.keys(response.data.episodes).map(key => ({
          name: `Season ${key}`,
          cover: cover,
        }));

        const result = seasonsFromEpisodes.map(season => ({
          name: season.name,
          cover: season.cover,
        }));

        resolve(result);
      } else {
        resolve([]);
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        reject(new Error("Network timeout - server took too long to respond"));
      } else if (error.response) {
        reject(new Error(`HTTP ${error.response.status}: ${error.response.statusText}`));
      } else {
        reject(new Error(`Network error: ${error.message}`));
      }
    }
  });
}

export function getCredits({ type, id }) {
  return api.get(`/${type}/${id}/credits`).then(({ cast }) => convertItemsToTiles(cast.slice(0, 7)));
}

// Module-level cache keyed by `${type}_${id}`.
// Persists for the whole session — returns the same reactive object so callers
// (Entity screen, player loading screen) share one set of already-populated signals.
const _infoCache = new Map();

/** Synchronous cache lookup — returns the cached result or null. */
export function getCachedInfo(type, id) {
  if (!type || !id) return null;
  return _infoCache.get(`${type}_${id}`) ?? null;
}

export function getInfo({ type, id, category_id }) {
  const cacheKey = `${type}_${id}`;
  if (id && _infoCache.has(cacheKey)) {
    return _infoCache.get(cacheKey);
  }

  let typeConst = "";
  if (type === "series") {
    typeConst = "series";
  } else {
    typeConst = "vod";
  }

  const [data, setData] = createSignal(null);
  const [title, setTitle] = createSignal(null);
  const [genre, setGenre] = createSignal(null);
  const [director, setDirector] = createSignal(null);
  const [duration, setDuration] = createSignal(null);
  const [cast, setCast] = createSignal(null);
  const [rating, setRating] = createSignal(null);
  const [release, setRelease] = createSignal(null);

  const [coverImage, setCoverImage] = createSignal(null);
  const [description, setDescription] = createSignal(null);
  const [runtime, setRuntime] = createSignal(null);
  const [releasedate, setReleasedate] = createSignal(null);
  const [movieimage, setMovieimage] = createSignal(null);
  const [seasons, setSeasons] = createSignal(null);
  const [idOfStreamm, setIdOfStreamm] = createSignal(null);
  const [containerExtencion, setContainerExtencion] = createSignal(null);

  createEffect(() => {
    const fetchData = async () => {
      const vpnStatus = localStorage.getItem("vpn");
      const proxyUrl = localStorage.getItem("proxy_url");

      let apiUrl = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_${typeConst}_info&${typeConst}_id=${id}`;

      // If VPN is on and proxy_url exists, buffer the API URL and use proxy
      if (vpnStatus === "on" && proxyUrl) {
        const bufferedUrl = Buffer.from(apiUrl, "utf-8").toString("base64");
        apiUrl = `${proxyUrl}${bufferedUrl}`;
      }

      try {
        const response = await axios.get(apiUrl, {
          headers: vpnStatus === "on" && proxyUrl ? getProxyHeaders() : undefined,
        });
        setData(response.data);
      } catch (error) {
        console.error(`❌ Error fetching info data for ${typeConst}:`, error);
      }
    };

    fetchData();
  });

  createEffect(() => {
    const currentData = data();

    if (currentData) {
      if (type === "series") {
        const imageUrl = Object.values(currentData.episodes)?.[0]?.[0]?.info?.movie_image;
        setGenre(currentData.info.genre);
        setRating(currentData.info.rating);

        setCoverImage(imageUrl);
        setDescription(currentData.info.plot);
        setGlobalStreamType("series");
        setSeasons(currentData.seasons);
        setTitle(currentData.info.name);
        // setContainerExtencion(currentData.info.container_extension);
      } else {
        setGenre(currentData.info.genre);
        setDirector(currentData.info.director);
        setDuration(currentData.info.duration);
        setCast(currentData.info.cast);
        setRating(currentData.info.rating);
        setRelease(currentData.info.releasedate);

        setTitle(currentData.movie_data.name);
        setContainerExtencion(currentData.movie_data.container_extension);

        setDescription(currentData.info.description || currentData.info.plot);
        // setGlobalStream(currentData.movie_data.stream_id);
        setIdOfStreamm(currentData.movie_data.stream_id);
        setGlobalStreamType("movie");
      }

      setRuntime(currentData.info.runtime);
      setReleasedate(currentData.info.releasedate);
      setMovieimage(currentData.info.backdrop_path);
    }
  });

  const result = {
    backgroundImage: movieimage,
    idOfStream: idOfStreamm,
    titleSecond: title,
    containerExtencion: containerExtencion,
    movieImage: movieimage,
    heroContent: {
      title: title,
      description: description,
      coverImage: coverImage,
      genre: genre,
      cast: cast,
      director: director,
      duration: duration,
      rating: rating,
      release: release,
      badges: ["HD", "CC"],
    },
    ...data,
  };

  if (id) _infoCache.set(cacheKey, result);
  return result;
}
